import { Formik } from 'formik';
import React, { useRef } from 'react';
import styled from 'styled-components';
import * as Yup from 'yup';
import { Button, Buttons, CancelButton, Field, Form, FormMessage, ModalCard } from '~/components';
import { useApi, useWorkspace } from '~/contexts';
import { useDirtyCheck, useForm } from '~/hooks';
import projectTaskCategoryTypes from '~/lookups/project-task-category-types';
import { ErrorPage } from '~/routes/public/pages';

const P = styled.p`
  &:not(:last-child) {
    margin-bottom: 1rem;
  }
`;

export default function ProjectTaskChangeCategoryConfirmation({ task, targetType, onClose, onChanged }) {
  const api = useApi();
  const { workspace } = useWorkspace();
  const [{ status, message, isSubmitting }, form] = useForm();
  const formRef = useRef();
  const dirtyCheck = useDirtyCheck(() => formRef.current.dirty);

  if (!task) return <ErrorPage.NotFound publicSite={false} />;

  const sourceLabel = task.type;
  const targetLabel = targetType;
  const targetTitle = projectTaskCategoryTypes[targetType].name;

  async function handleSubmit(values) {
    try {
      form.submit();
      await api.www
        .workspaces(workspace.id)
        .projects(task.projectId)
        .tasks(task.id)
        .setTaskCategory({ projectTaskCategoryId: values.taskCategory?.id ?? null });
      await onChanged();
      form.done();
    } catch ({ message }) {
      form.error({ message });
    }
  }

  const initialValues = { taskCategory: null };
  const handleClose = () => dirtyCheck(onClose);

  return (
    <ModalCard title={`Change to ${targetTitle} Task`} onClose={onClose}>
      <Formik
        innerRef={formRef}
        enableReinitialize
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validateOnBlur={false}
        validateOnChange={false}
        validationSchema={Yup.object().shape({
          taskCategory: Yup.object().label('Task Category').nullable().required(),
        })}>
        {() => (
          <Form>
            <ModalCard.Body>
              <P>
                This will change the <strong>{task.name}</strong> task from a {sourceLabel} task to a {targetLabel}{' '}
                task. Select the new task category below.
              </P>
              <Form.Control>
                <Field.ProjectTaskCategorySelect
                  autoFocus
                  name="taskCategory"
                  placeholder={`${targetTitle} Task Category`}
                  type={targetType}
                  clearable={false}
                />
              </Form.Control>
              {status && <FormMessage.Error>{message || 'An error has occurred.'}</FormMessage.Error>}
            </ModalCard.Body>
            <ModalCard.Footer>
              <Buttons align="right">
                <CancelButton onClick={handleClose}>Close</CancelButton>
                <Button type="submit" isLoading={isSubmitting}>
                  Change
                </Button>
              </Buttons>
            </ModalCard.Footer>
          </Form>
        )}
      </Formik>
    </ModalCard>
  );
}
